<div class="rules">
    <!-- ============================== scope toolbar ==============================
         A horizontal toolbar rather than a side column: the matrix needs the full
         width, and when this component is embedded in a client/partner detail page
         the picker is hidden entirely — a grid column would leave the matrix
         squeezed into the empty picker's width. -->
    @if (!isEmbedded()) {
        <div class="scope-bar">
            <div class="field">
                <label>Apply rules to</label>
                <p-select [ngModel]="scopeType()" (ngModelChange)="onScopeTypeChange($event)"
                    [options]="scopeTypes" optionLabel="label" optionValue="value"
                    appendTo="body" styleClass="scope-select" />
            </div>

            @if (!isGlobal()) {
                <div class="field grow">
                    <label>{{ scopeType() === 'PARTNER' ? 'Partner' : 'Client' }}</label>
                    <p-select [ngModel]="scopeId()" (ngModelChange)="pickScope($event)"
                        [options]="scopes()" optionLabel="label" optionValue="scopeId"
                        [filter]="true" filterBy="label" [showClear]="true"
                        [loading]="loadingScopes()"
                        [placeholder]="scopesPlaceholder()"
                        [emptyMessage]="scopesEmptyMessage()"
                        appendTo="body" styleClass="scope-select" />
                </div>
            }

            @if (isGlobal()) {
                <p class="hint">
                    @if (isAdmin()) {
                        Internal alerts to your own team — sent to the recipient list below,
                        not to customers.
                    } @else {
                        The defaults every partner and client follows unless they have their own rules.
                    }
                </p>
            }
        </div>

        @if (scopeError()) {
            <div class="warn-strip err">
                <i class="pi pi-times-circle"></i>
                <span>{{ scopeError() }}</span>
            </div>
        }
    }

    <!-- ============================== the matrix ============================== -->
    <div class="matrix-pane">
        @if (loading()) {
            <div class="empty"><i class="pi pi-spin pi-spinner"></i> Loading…</div>
        } @else if (!matrix()) {
            <div class="empty">
                @if (isAdmin()) { Could not load the admin alert rules. }
                @else if (isGlobal()) { Could not load the global defaults. }
                @else { Select a partner or client to configure. }
            </div>
        } @else {
            <div class="matrix-head">
                <div class="mh-text">
                    <h3>{{ matrix()!.scopeName }}</h3>
                    <p>
                        A message is delivered <b>only</b> on the channels ticked here. There is no
                        automatic SMS fallback.
                        @if (!isGlobal()) { Rows left on <b>Inherit</b> follow the global defaults. }
                    </p>
                </div>
                <div class="head-actions">
                    @if (!isGlobal()) {
                        <p-button label="Reset to global" icon="pi pi-undo" [outlined]="true" size="small"
                            (onClick)="resetAll()" />
                    }
                    <p-button [label]="saving() ? 'Saving…' : 'Save rules'" icon="pi pi-check"
                        [disabled]="!dirty()" [loading]="saving()" (onClick)="save()" />
                </div>
            </div>

            @if (missingProviders().length) {
                <div class="warn-strip">
                    <i class="pi pi-exclamation-triangle"></i>
                    <span>
                        No provider is configured for
                        <b>{{ missingProviders().join(', ') }}</b>. Messages assigned to
                        {{ missingProviders().length > 1 ? 'those channels' : 'that channel' }} will wait
                        in the outbox until one is set up in the Providers tab.
                    </span>
                </div>
            }

            <div class="matrix-scroll">
                <table class="matrix">
                    <thead>
                        <tr>
                            <th class="ta-l">Message</th>
                            @for (c of channels(); track c) { <th class="ta-c">{{ c }}</th> }
                            <th class="ta-c">On</th>
                            @if (!isGlobal()) { <th class="ta-c">Inherit</th> }
                        </tr>
                    </thead>
                    <tbody>
                        @for (t of matrix()!.types; track t.messageType) {
                            <tr [class.locked-row]="t.isLocked" [class.inherited-row]="t.inherited">
                                <td class="ta-l">
                                    <span class="mt-name">
                                        {{ t.name || t.messageType }}
                                        @if (t.isLocked) {
                                            <i class="pi pi-lock" pTooltip="Always sent — disabling this would lock users out of the platform."></i>
                                        }
                                    </span>
                                    @if (t.description) { <small class="muted">{{ t.description }}</small> }
                                </td>

                                @for (c of channels(); track c) {
                                    <td class="ta-c">
                                        <p-toggleSwitch [ngModel]="t.isLocked || t.channels[c]"
                                            [disabled]="t.isLocked || t.inherited"
                                            (ngModelChange)="toggle(t.messageType, c, $event)" />
                                    </td>
                                }

                                <td class="ta-c">
                                    @if (t.isLocked) {
                                        <p-tag severity="success" value="always" />
                                    } @else if (rowCount(t) === 0) {
                                        <p-tag severity="danger" value="off"
                                            pTooltip="Nothing will be sent for this message." />
                                    } @else {
                                        <p-tag severity="success" [value]="rowCount(t) + ''" />
                                    }
                                </td>

                                @if (!isGlobal()) {
                                    <td class="ta-c">
                                        <p-toggleSwitch [ngModel]="t.inherited" [disabled]="t.isLocked"
                                            (ngModelChange)="toggleInherit(t.messageType, $event)"
                                            pTooltip="Follow the global defaults for this message" />
                                    </td>
                                }
                            </tr>
                        }
                    </tbody>
                </table>
            </div>
        }
    </div>

    <!-- ============================== admin recipients ==============================
         Deliberately NOT derived from the users table: creating an admin account must not
         silently subscribe that person to operational alerts, and the list has to survive
         staff changes. Login OTP ignores this list — it goes to the admin signing in. -->
    @if (isAdmin() && !isEmbedded()) {
        <div class="matrix-pane">
            <div class="matrix-head">
                <div class="mh-text">
                    <h3>Who receives these alerts</h3>
                    <p>
                        Alerts go to every address listed here on the channels ticked above.
                        <b>Admin login OTP is the exception</b> — that always goes to the admin
                        signing in, never to this list.
                    </p>
                </div>
                <div class="head-actions">
                    <p-tag [severity]="selectedCount() ? 'success' : 'danger'"
                        [value]="selectedCount() + ' selected'" />
                    <p-button [label]="savingRecipients() ? 'Saving…' : 'Save list'" icon="pi pi-check"
                        [disabled]="!recipientsDirty()" [loading]="savingRecipients()"
                        (onClick)="saveRecipients()" />
                </div>
            </div>

            <div class="matrix-scroll">
                <table class="matrix">
                    <thead>
                        <tr>
                            <th class="ta-c" style="width:80px">Receives</th>
                            <th class="ta-l" style="width:180px">Admin</th>
                            <th class="ta-l">Contact on file</th>
                            <th class="ta-c" style="width:150px">Extra numbers</th>
                        </tr>
                    </thead>
                    <tbody>
                        @for (r of recipients(); track r.userId) {
                            <tr [class.inherited-row]="!r.isSelected">
                                <td class="ta-c">
                                    <p-toggleSwitch [ngModel]="r.isSelected"
                                        (ngModelChange)="toggleRecipient(r.userId, $event)" />
                                </td>
                                <td class="ta-l"><span class="mt-name">{{ r.username }}</span></td>
                                <td class="ta-l">
                                    <small class="muted" [class.warn-text]="!r.email && !r.mobile">
                                        {{ contactFor(r) }}
                                    </small>
                                </td>
                                <td class="ta-c">
                                    <p-button label="Manage" icon="pi pi-mobile" [text]="true" size="small"
                                        (onClick)="openContacts(r)" />
                                </td>
                            </tr>
                        } @empty {
                            <tr>
                                <td colspan="4" class="empty">No active Admin accounts found.</td>
                            </tr>
                        }
                    </tbody>
                </table>
            </div>

            @if (selectedCount() === 0) {
                <div class="warn-strip" style="margin-top:12px; margin-bottom:0;">
                    <i class="pi pi-exclamation-triangle"></i>
                    <span>No admin is selected, so these alerts will not reach anyone.</span>
                </div>
            }
        </div>

        <!-- Extra contacts for one admin. users has a single email + mobile; this is where a
             second phone or a shared ops number goes. Applies to the sign-in code as well as
             alerts. -->
        <p-dialog [visible]="contactsFor() !== null" (visibleChange)="!$event && closeContacts()"
            [modal]="true" [draggable]="false" [style]="{ width: '640px' }"
            [breakpoints]="{ '720px': '96vw' }"
            [header]="'Extra contacts — ' + (contactsFor()?.username || '')">
            @if (contactsFor(); as u) {
                <p class="hint" style="margin-bottom:12px">
                    Their account contact ({{ contactFor(u) }}) is always used. Anything added here
                    is used <b>as well</b> — the sign-in code and any admin alerts go to every one
                    of them, on the channels enabled above.
                </p>
            }

            <table class="matrix">
                <thead>
                    <tr>
                        <th class="ta-l" style="width:150px">Channel</th>
                        <th class="ta-l">Number / address</th>
                        <th class="ta-l" style="width:150px">Label</th>
                        <th class="ta-c" style="width:56px"></th>
                    </tr>
                </thead>
                <tbody>
                    @for (c of contacts(); track $index) {
                        <tr>
                            <td>
                                <p-select [ngModel]="c.channel" [options]="channelOptions"
                                    (ngModelChange)="patchContact($index, { channel: $event })"
                                    appendTo="body" styleClass="scope-select" />
                            </td>
                            <td>
                                <input pInputText type="text" [ngModel]="c.contact" style="width:100%"
                                    (ngModelChange)="patchContact($index, { contact: $event })"
                                    [placeholder]="c.channel === 'EMAIL' ? 'name@example.com' : '9876543210'" />
                            </td>
                            <td>
                                <input pInputText type="text" [ngModel]="c.label" style="width:100%"
                                    (ngModelChange)="patchContact($index, { label: $event })"
                                    placeholder="Work phone" />
                            </td>
                            <td class="ta-c">
                                <p-button icon="pi pi-trash" [text]="true" severity="danger" size="small"
                                    (onClick)="removeContact($index)" />
                            </td>
                        </tr>
                    } @empty {
                        <tr><td colspan="4" class="empty">No extra contacts — only the account contact is used.</td></tr>
                    }
                </tbody>
            </table>

            <div class="modal-actions" style="display:flex; justify-content:space-between; gap:8px; margin-top:14px">
                <p-button label="Add number" icon="pi pi-plus" [outlined]="true" size="small"
                    (onClick)="addContact()" />
                <span style="display:flex; gap:8px">
                    <p-button label="Cancel" [outlined]="true" (onClick)="closeContacts()" />
                    <p-button [label]="savingContacts() ? 'Saving…' : 'Save'" icon="pi pi-check"
                        [loading]="savingContacts()" (onClick)="saveContacts()" />
                </span>
            </div>
        </p-dialog>
    }
</div>
